import { create } from 'zustand';
import axios from 'axios';
import { API_URL } from '../constants';

const api = axios.create({ baseURL: API_URL });

// Middleware para incluir el token en cada petición
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const useStore = create((set, get) => ({
  user: JSON.parse(localStorage.getItem('user')) || null,
  products: [],
  orders: [],
  loading: false,

  // --- AUTH ---
  login: async (credentials) => {
    const { data } = await api.post('/auth/login', credentials);
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data));
    set({ user: data });
  },
  register: async (userData) => {
    set({ loading: true });
    try {
      // Al usar 'api', ya apunta al puerto 5000 automáticamente
      const { data } = await api.post('/auth/register', userData);
      set({ loading: false });
      return data;
    } catch (error) {
      set({ loading: false });
      throw error;
    }
  },
  logout: () => {
    localStorage.clear();
    set({ user: null, products: [], orders: [] });
  },

  // --- PRODUCTS ---
  fetchProducts: async () => {
    const { data } = await api.get('/products');
    set({ products: data });
  },

  addProduct: async (formData) => {
    // IMPORTANTE: formData ya es un objeto FormData que viene del componente.
    // No uses {...formData} porque eso devuelve un objeto vacío {}.
    
    const { data } = await api.post('/products', formData, {
      headers: { 
        // Al enviar FormData, Axios configura automáticamente el Content-Type 
        // con el boundary necesario para archivos.
        'Content-Type': 'multipart/form-data' 
      }
    });
    
    set((state) => ({ products: [...state.products, data] }));
  },

  updateProduct: async (id, formData) => {
    const { data } = await api.put('/products/'+id, formData, {
      headers: { 
        'Content-Type': 'multipart/form-data' 
      }
    });
    
    set((state) => ({
      products: state.products.map((p) => (p._id === id ? data : p))
    }));
  },
  fetchAllAgentProducts: async () => {
    try {
      const response = await api.get('/products/agent/products');
      const data = await response.json();
      set({ products: data });
    } catch (error) {
      console.error('Error fetching agent products:', error);
    }
  },
  fetchProductDetails: async (id) => {
    try {
      // 1. Usamos 'api' (Axios). No necesitas await response.json()
      const { data } = await api.get(`/products/agent/product/${id}`);
      
      // 2. Axios ya parseó el JSON, así que retornamos 'data' directamente
      return data;
    } catch (error) {
      console.error('Error fetching product details:', error);
      throw error;
    }
  },
  // --- ORDERS ---
  createOrder: async (orderData) => {
    set({ loading: true });
    try {
      const { data } = await api.post('/orders', orderData);
      
      // Opcional: Si el usuario tiene una lista de sus pedidos, la actualizamos
      set((state) => ({ 
        orders: [...state.orders, data],
        loading: false 
      }));
      
      return data;
    } catch (error) {
      set({ loading: false });
      throw error;
    }
  },
  fetchOrders: async () => {
    const { data } = await api.get('/orders');
    set({ orders: data });
  },
  updateOrderStatus: async (id, estado) => {
    // Implementar endpoint en back si no existe
    await api.put(`/orders/${id}`, { estado });
    get().fetchOrders();
  }
}));

export default useStore;